"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  Clock,
  Columns,
  Eye,
  FileEdit,
  FileText,
  Hash,
  History,
  Loader2,
  MessageSquare,
  Network,
  Plus,
  Search,
  Share2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useWorkspace, WorkspaceFile } from "@/lib/workspace-context";
import { useFilesStore } from "@/stores/files-store";
import { toast } from "@/components/ui/toast";
import { AIAssistantPanel } from "@/features/ai/ai-assistant-panel";
import { useCollab } from "./use-collab";
import { EditorToolbar } from "./EditorToolbar";
import { MarkdownPreview } from "./MarkdownPreview";
import { BacklinksPanel } from "./BacklinksPanel";
import { ConceptFlowMap } from "./ConceptFlowMap";
import { QuickTaskModal } from "./QuickTaskModal";
import { SlashCommandMenu, type SlashCommand } from "./SlashCommandMenu";
import { FloatingFormatMenu } from "./FloatingFormatMenu";
import { WikiLinkAutocomplete, buildWikiLinkSuggestions } from "./WikiLinkAutocomplete";
import { EditorBreadcrumb } from "./EditorBreadcrumb";
import { EditorMetadataTags } from "./EditorMetadataTags";
import {
  DocumentCommentsModal,
  ShareDocumentModal,
  VersionHistoryModal,
} from "./EditorDocumentPanels";

interface SimpleEditorProps {
  pane: "left" | "right";
}

export default function SimpleEditor({ pane }: SimpleEditorProps) {
  const {
    files,
    leftPane,
    rightPane,
    activePane,
    setActivePane,
    openFile,
    closeFile,
    setActiveTab,
    setEditorMode,
    setPaneViewType,
    reorderTabs,
    updateFileContent,
    updateFileTitle,
    createFile,
    isSplit,
    setIsSplit,
    getBacklinks,
  } = useWorkspace();

  const saveStatus = useFilesStore((s) => s.saveStatus);
  const updateFileTags = useFilesStore((s) => s.updateFileTags);
  const hydrateFileContent = useFilesStore((s) => s.hydrateFileContent);

  const paneState = pane === "left" ? leftPane : rightPane;
  const activeFile = files.find((f) => f.id === paneState.activeFileId);
  const backlinks = activeFile ? getBacklinks(activeFile.id) : [];
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [titleInput, setTitleInput] = useState("");
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [slashMenu, setSlashMenu] = useState<{ query: string; position: { x: number; y: number } } | null>(null);
  const [floatingFormat, setFloatingFormat] = useState<{ position: { x: number; y: number } | null }>({ position: null });
  const [wikiLinkMenu, setWikiLinkMenu] = useState<{ query: string; position: { x: number; y: number } } | null>(null);
  const { activeUsers, applyLocalDiff } = useCollab({
    documentId: activeFile?.documentId,
    fileId: activeFile?.id ?? "",
    initialContent: activeFile?.content ?? "",
    enabled: paneState.editorMode === "edit",
    onRemoteContent: (newContent) => {
      if (activeFile) updateFileContent(activeFile.id, newContent);
    },
  });

  useEffect(() => {
    if (activeFile) {
      setTitleInput(activeFile.name.replace(".md", ""));
    }
  }, [paneState.activeFileId, activeFile]);

  useEffect(() => {
    if (textareaRef.current && paneState.editorMode === "edit") {
      textareaRef.current.focus();
    }
  }, [paneState.activeFileId, paneState.editorMode]);

  const conceptMapData = useMemo(() => {
    if (!activeFile || !activeFile.content) return { headings: [], outgoing: [] };

    const headings: string[] = [];
    const lines = activeFile.content.split("\n");
    lines.forEach((line) => {
      const match = line.match(/^(#+)\s+(.*)$/);
      if (match) {
        headings.push(match[2].trim());
      }
    });

    const outgoingIds = new Set<string>();
    const outgoingFiles: WorkspaceFile[] = [];
    const regex = /\[\[([^\]]+)\]\]/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(activeFile.content)) !== null) {
      const name = match[1].trim();
      const cleanSearchName = name.replace(".md", "").toLowerCase().trim();
      const found = files.find(
        (f) =>
          f.type === "file" &&
          (f.name.replace(".md", "").toLowerCase().trim() === cleanSearchName ||
            f.name.toLowerCase().trim() === cleanSearchName),
      );
      if (found && !outgoingIds.has(found.id)) {
        outgoingIds.add(found.id);
        outgoingFiles.push(found);
      }
    }

    return {
      headings: headings.slice(0, 5),
      outgoing: outgoingFiles,
    };
  }, [activeFile, files]);

  const wordCount = useMemo(() => {
    if (!activeFile?.content) return 0;
    return activeFile.content.split(/\s+/).filter(Boolean).length;
  }, [activeFile?.content]);

  const readingMinutes = useMemo(() => {
    if (wordCount === 0) return 0;
    // ~200 palabras por minuto en español
    return Math.max(1, Math.round(wordCount / 200));
  }, [wordCount]);

  if (!activeFile) {
    return <WelcomeView pane={pane} />;
  }

  const handleQuickTask = () => {
    if (!activeFile?.documentId) {
      toast.warning(
        "Documento no sincronizado",
        "Esta nota aún no tiene un documento en el servidor. Guarda la nota primero.",
      );
      return;
    }
    setQuickTaskOpen(true);
  };

  const handleAddTag = (tag: string) => {
    if (!activeFile) return;
    const currentTags = activeFile.tags || [];
    if (!currentTags.includes(tag)) {
      updateFileTags(activeFile.id, [...currentTags, tag]);
    }
  };

  const handleRemoveTag = (tag: string) => {
    if (!activeFile) return;
    const currentTags = activeFile.tags || [];
    updateFileTags(
      activeFile.id,
      currentTags.filter((t) => t !== tag),
    );
  };

  const handleCreateWikiLink = (linkName: string) => {
    const newId = createFile(linkName);
    openFile(newId, pane);
    toast.success("Nota creada", `Se creó "${linkName}" y se abrió en el panel.`);
  };

  const insertMarkdown = (syntaxBefore: string, syntaxAfter: string = "") => {
    const textarea = textareaRef.current;
    if (!textarea || !activeFile) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);
    const replacement = syntaxBefore + selectedText + syntaxAfter;

    const newContent = text.substring(0, start) + replacement + text.substring(end);
    updateFileContent(activeFile.id, newContent);
    applyLocalDiff(text, newContent);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + syntaxBefore.length,
        start + syntaxBefore.length + selectedText.length,
      );
    }, 50);
  };

  const getCaretCoordinates = (ta: HTMLTextAreaElement, charIndex: number) => {
    const rect = ta.getBoundingClientRect();
    const before = ta.value.slice(0, charIndex);
    const lines = before.split("\n");
    const lineIndex = lines.length - 1;
    const currentLineLength = lines[lines.length - 1]?.length ?? 0;
    const lineHeight = 23; // ~14.5px * 1.6 leading
    const rawY = rect.top + Math.min(rect.height - 40, Math.max(16, (lineIndex + 1) * lineHeight - ta.scrollTop));
    const rawX = rect.left + Math.min(rect.width - 220, Math.max(16, currentLineLength * 7.5));
    return { x: rawX, y: rawY };
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!activeFile) return;
    const newVal = e.target.value;
    const oldVal = activeFile.content || "";

    updateFileContent(activeFile.id, newVal);
    applyLocalDiff(oldVal, newVal);

    // Detect slash command trigger: line starts with `/` (and nothing else after the slash)
    if (e.target instanceof HTMLTextAreaElement) {
      const ta = e.target;
      const cursor = ta.selectionStart;
      const before = newVal.slice(0, cursor);
      const lastLine = before.split("\n").pop() ?? "";
      const slashIdx = lastLine.lastIndexOf("/");
      if (slashIdx !== -1) {
        const after = lastLine.slice(slashIdx + 1);
        if (!/\s/.test(after) && lastLine.trim() === lastLine.slice(slashIdx)) {
          setSlashMenu({
            query: after,
            position: getCaretCoordinates(ta, cursor),
          });
          setWikiLinkMenu(null);
          return;
        }
      }
      setSlashMenu(null);

      // Detect [[wiki-link]] trigger: cursor is right after `[[`
      const wikiLinkMatch = lastLine.match(/\[\[([^\]]*)$/);
      if (wikiLinkMatch) {
        setWikiLinkMenu({
          query: wikiLinkMatch[1],
          position: getCaretCoordinates(ta, cursor),
        });
        return;
      }
      setWikiLinkMenu(null);
    }
  };

  const handleTextareaSelect = () => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    if (start === end) {
      setFloatingFormat({ position: null });
      return;
    }
    const coords = getCaretCoordinates(ta, start);
    setFloatingFormat({
      position: {
        x: coords.x,
        y: Math.max(64, coords.y - 44),
      },
    });
  };

  const handleSlashSelect = (cmd: SlashCommand) => {
    if (!activeFile || !textareaRef.current) return;
    const ta = textareaRef.current;
    const cursor = ta.selectionStart;
    const value = ta.value;
    const before = value.slice(0, cursor);
    const lastLine = before.split("\n").pop() ?? "";
    const slashIdx = lastLine.lastIndexOf("/");
    if (slashIdx === -1) return;

    const start = cursor - (lastLine.length - slashIdx);
    const newValue = value.slice(0, start) + cmd.insert + value.slice(cursor);
    updateFileContent(activeFile.id, newValue);
    applyLocalDiff(value, newValue);
    setSlashMenu(null);

    // Re-focus textarea and place caret after inserted text
    requestAnimationFrame(() => {
      ta.focus();
      const newCursor = start + cmd.insert.length;
      ta.setSelectionRange(newCursor, newCursor);
    });
  };

  const handleWikiLinkSelect = (suggestion: { file: { id: string; name: string }; exists: boolean }) => {
    if (!activeFile || !textareaRef.current) return;
    const ta = textareaRef.current;
    const cursor = ta.selectionStart;
    const value = ta.value;
    const before = value.slice(0, cursor);
    const lastLine = before.split("\n").pop() ?? "";
    const match = lastLine.match(/\[\[([^\]]*)$/);
    if (!match) return;

    const linkStart = cursor - match[0].length;
    const targetName = suggestion.file.name.replace(".md", "");
    const replacement = suggestion.exists ? `[[${targetName}]]` : `[[${targetName}]]`;
    const newValue = value.slice(0, linkStart) + replacement + value.slice(cursor);
    updateFileContent(activeFile.id, newValue);
    applyLocalDiff(value, newValue);
    setWikiLinkMenu(null);

    if (!suggestion.exists) {
      const newId = createFile(targetName);
      openFile(newId, pane);
      toast.success("Nota creada", `Se creó "${targetName}" y se abrió en el panel.`);
    }

    requestAnimationFrame(() => {
      ta.focus();
      const newCursor = linkStart + replacement.length;
      ta.setSelectionRange(newCursor, newCursor);
    });
  };

  const handleTitleBlur = () => {
    if (!activeFile) return;
    if (titleInput.trim() && titleInput !== activeFile.name.replace(".md", "")) {
      updateFileTitle(activeFile.id, titleInput.trim());
    }
  };

  return (
    <div
      className={cn(
        "flex-1 flex flex-col min-w-0 h-full bg-background transition-all border-r border-border/10",
        activePane === pane ? "ring-1 ring-primary/20 bg-background/90" : "opacity-90",
      )}
      onClick={() => setActivePane(pane)}
    >
      {/* Tab bar */}
      <div
        className="h-9 flex items-center bg-secondary/20 border-b border-border/40 select-none overflow-x-auto custom-scrollbar shrink-0"
        data-tour="editor-tabs"
      >
        <div className="flex items-center flex-1">
          {paneState.openTabs.map((tabId, index) => {
            const f = files.find((file) => file.id === tabId);
            if (!f) return null;
            const isTabActive = paneState.activeFileId === tabId && paneState.viewType === "editor";
            return (
              <div
                key={tabId}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/x-wiki-tab-index", String(index));
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes("application/x-wiki-tab-index")) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={(e) => {
                  const from = Number(e.dataTransfer.getData("application/x-wiki-tab-index"));
                  if (Number.isNaN(from) || from === index) return;
                  e.preventDefault();
                  reorderTabs(from, index, pane);
                }}
                className={cn(
                  "h-9 px-3 flex items-center gap-1.5 border-r border-border/40 text-xs font-medium cursor-pointer transition-all relative shrink-0",
                  isTabActive
                    ? "bg-background text-foreground border-b-2 border-b-primary"
                    : "text-muted-foreground hover:bg-secondary/40 hover:text-foreground",
                )}
                onClick={() => {
                  setPaneViewType("editor", pane);
                  setActiveTab(tabId, pane);
                }}
              >
                <FileText
                  className={cn("w-3.5 h-3.5", isTabActive ? "text-primary" : "text-muted-foreground/60")}
                />
                <span>{f.name.replace(".md", "")}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeFile(tabId, pane);
                  }}
                  className="p-0.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-all ml-1.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-1 px-2 border-l border-border/40 shrink-0">
          <button
            onClick={() => {
              const newId = createFile("Nota sin título");
              openFile(newId, pane);
            }}
            className="p-1 hover:bg-secondary/50 rounded text-muted-foreground hover:text-foreground transition-all"
            title="Nueva nota"
          >
            <Plus className="w-4 h-4" />
          </button>

          <button
            onClick={() => setIsSplit(!isSplit)}
            className={cn(
              "p-1 hover:bg-secondary/50 rounded transition-all",
              isSplit ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground",
            )}
            title={isSplit ? "Unir paneles" : "Dividir panel"}
          >
            <Columns className="w-4 h-4" />
          </button>

          <button
            onClick={() => setPaneViewType("graph", pane)}
            className="p-1 hover:bg-secondary/50 rounded text-muted-foreground hover:text-foreground transition-all"
            title="Ver Red"
          >
            <Hash className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Breadcrumbs & Actions Header */}
      <header className="h-10 flex items-center justify-between px-4 border-b border-border/20 bg-background/30 backdrop-blur-sm select-none shrink-0">
        {activeFile ? (
          <EditorBreadcrumb file={activeFile} />
        ) : (
          <span className="text-[11px] text-muted-foreground">Inicio</span>
        )}

        <div className="flex items-center gap-2" data-tour="editor-toolbar">
          {activeUsers.length > 0 && (
            <div className="flex items-center -space-x-1.5 mr-2">
              {activeUsers.map((u, i) => (
                <div
                  key={u.userId || i}
                  className="w-5 h-5 rounded-full border border-background bg-primary/20 text-primary text-[8px] font-bold flex items-center justify-center cursor-default uppercase shrink-0"
                  title={`${u.userName} está colaborando`}
                >
                  {u.userName ? u.userName.slice(0, 2) : "Ag"}
                </div>
              ))}
            </div>
          )}

          {paneState.editorMode === "edit" && (
            <EditorToolbar
              onBold={() => insertMarkdown("**", "**")}
              onItalic={() => insertMarkdown("*", "*")}
              onWikiLink={() => insertMarkdown("[[", "]]")}
              onCode={() => insertMarkdown("`", "`")}
              onCreateTask={handleQuickTask}
              taskDisabled={!activeFile?.documentId}
            />
          )}

          {/* Document Action Buttons: Comments, Version History, Share/Template */}
          <div className="flex items-center gap-0.5 border-l border-border/30 pl-1.5">
            <button
              onClick={() => setCommentsOpen(true)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
              title="Comentarios de la nota"
              aria-label="Comentarios de la nota"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setHistoryOpen(true)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
              title="Historial de versiones"
              aria-label="Historial de versiones"
            >
              <History className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setShareOpen(true)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
              title="Compartir enlace público o guardar como plantilla"
              aria-label="Compartir nota"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Edit/Preview/Flow Switcher */}
          <div
            className="flex items-center bg-secondary/50 rounded-lg p-0.5 border border-border/40"
            role="tablist"
            aria-label="Modo de visualización"
          >
            <button
              onClick={() => setEditorMode("edit", pane)}
              className={cn(
                "px-2 py-1 rounded-md transition-all flex items-center gap-1 text-[11px] font-medium",
                paneState.editorMode === "edit"
                  ? "bg-background shadow-sm text-primary font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
              title="Modo Edición"
              aria-label="Modo edición"
              aria-selected={paneState.editorMode === "edit"}
              role="tab"
            >
              <FileEdit className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Editar</span>
            </button>
            <button
              onClick={() => setEditorMode("preview", pane)}
              className={cn(
                "px-2 py-1 rounded-md transition-all flex items-center gap-1 text-[11px] font-medium",
                paneState.editorMode === "preview"
                  ? "bg-background shadow-sm text-primary font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
              title="Modo Previsualización"
              aria-label="Modo previsualización"
              aria-selected={paneState.editorMode === "preview"}
              role="tab"
            >
              <Eye className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Lectura</span>
            </button>
            <button
              onClick={() => setEditorMode("flow", pane)}
              className={cn(
                "px-2 py-1 rounded-md transition-all flex items-center gap-1 text-[11px] font-medium",
                paneState.editorMode === "flow"
                  ? "bg-background shadow-sm text-primary font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
              title="Ver Mapa Conceptual"
              aria-label="Mapa conceptual"
              aria-selected={paneState.editorMode === "flow"}
              role="tab"
            >
              <Network className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Flujo</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
        <div className="max-w-3xl mx-auto flex flex-col h-full min-h-[50vh]">
          {paneState.editorMode === "flow" ? (
            <ConceptFlowMap
              fileName={activeFile.name}
              headings={conceptMapData.headings}
              outgoing={conceptMapData.outgoing}
              backlinks={backlinks}
              onNodeClick={(id) => openFile(id, pane)}
            />
          ) : (
            <>
              <EditorMetadataTags
                file={activeFile}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
              />

              <input
                type="text"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                onBlur={handleTitleBlur}
                disabled={paneState.editorMode === "preview"}
                placeholder="Título de la nota..."
                className="w-full bg-transparent text-3xl md:text-4xl font-bold tracking-tight text-foreground outline-none mb-6 placeholder:text-muted-foreground/20 leading-tight border-b border-transparent focus:border-border/30 pb-2 transition-all disabled:opacity-90"
              />

              <div className="flex-1 flex flex-col min-h-[350px]">
                {paneState.editorMode === "edit" ? (
                  <textarea
                    ref={textareaRef}
                    value={activeFile.content || ""}
                    onChange={handleTextareaChange}
                    onSelect={handleTextareaSelect}
                    onKeyUp={handleTextareaSelect}
                    onClick={handleTextareaSelect}
                    placeholder="Comienza a escribir en Markdown, escribe / para comandos o usa [[Nota]] para enlazar..."
                    className="w-full flex-1 bg-transparent text-[14.5px] leading-relaxed text-foreground/90 outline-none resize-none placeholder:text-muted-foreground/30 font-sans border-none"
                    spellCheck={false}
                  />
                ) : (
                  <MarkdownPreview
                    file={activeFile}
                    onOpen={(fileId) => openFile(fileId, pane)}
                    onCreateWikiLink={handleCreateWikiLink}
                  />
                )}
              </div>

              <BacklinksPanel file={activeFile} onOpen={(fileId) => openFile(fileId, pane)} />
              {slashMenu && (
                <SlashCommandMenu
                  query={slashMenu.query}
                  position={slashMenu.position}
                  onSelect={handleSlashSelect}
                  onClose={() => setSlashMenu(null)}
                />
              )}
              {wikiLinkMenu && (
                <WikiLinkAutocomplete
                  query={wikiLinkMenu.query}
                  position={wikiLinkMenu.position}
                  suggestions={buildWikiLinkSuggestions(wikiLinkMenu.query, files)}
                  onSelect={handleWikiLinkSelect}
                  onClose={() => setWikiLinkMenu(null)}
                />
              )}
            </>
          )}
        </div>
      </div>

      <FloatingFormatMenu
        position={floatingFormat.position}
        visible={floatingFormat.position !== null}
        onBold={() => insertMarkdown("**", "**")}
        onItalic={() => insertMarkdown("*", "*")}
        onLink={() => insertMarkdown("[", "](url)")}
        onCode={() => insertMarkdown("`", "`")}
      />

      {/* AI Assistant */}
      <AIAssistantPanel
        documentId={activeFile.documentId || activeFile.id}
        documentTitle={activeFile.name}
        documentContent={activeFile.content || ""}
        onInsert={(text) => {
          const current = activeFile.content || "";
          const textarea = textareaRef.current;
          let next: string;
          let cursor: number;
          if (textarea && paneState.editorMode === "edit" && document.activeElement === textarea) {
            const start = textarea.selectionStart ?? current.length;
            const end = textarea.selectionEnd ?? current.length;
            const selected = current.slice(start, end);
            const selectedText = selected.length > 0 ? selected : "";
            const replacement = selectedText ? text : (current.length > 0 && start > 0 ? "\n\n" : "") + text;
            next = current.slice(0, start) + replacement + current.slice(end);
            cursor = start + replacement.length;
          } else {
            const needsSeparator = current.length > 0 && !current.endsWith("\n\n");
            next = current + (needsSeparator ? "\n\n" : "") + text;
            cursor = next.length;
          }
          updateFileContent(activeFile.id, next);
          requestAnimationFrame(() => {
            const ta = textareaRef.current;
            if (ta) {
              ta.focus();
              ta.setSelectionRange(cursor, cursor);
            }
          });
        }}
        onReplace={(text) => {
          updateFileContent(activeFile.id, text);
        }}
      />

      {/* Quick task modal */}
      <QuickTaskModal
        open={quickTaskOpen}
        onClose={() => setQuickTaskOpen(false)}
        documentId={activeFile.documentId}
      />

      {/* Version History, Comments, and Share Modals */}
      <VersionHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        documentId={activeFile.documentId}
        onRestored={(restoredTitle, restoredContent) => {
          hydrateFileContent(activeFile.id, restoredContent);
          if (restoredTitle && restoredTitle !== activeFile.name.replace(".md", "")) {
            updateFileTitle(activeFile.id, restoredTitle);
          }
        }}
      />
      <DocumentCommentsModal
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        documentId={activeFile.documentId}
      />
      <ShareDocumentModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        documentId={activeFile.documentId}
        documentTitle={activeFile.name}
        documentContent={activeFile.content || ""}
      />

      {/* Pane Footer */}
      <footer className="h-8 border-t border-border/20 px-4 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50 bg-secondary/10 shrink-0 select-none">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setHistoryOpen(true)}
            title={activeFile?.lastModified ? `Última edición: ${activeFile.lastModified} (Clic para ver historial)` : "Ver historial de versiones"}
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
          >
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="w-2.5 h-2.5 animate-spin text-primary" />
                <span className="text-primary">Guardando...</span>
              </>
            ) : saveStatus === "error" ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                <span className="text-destructive">Error al guardar</span>
              </>
            ) : (
              <>
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    activeFile?.lastModified || saveStatus === "saved"
                      ? "bg-green-500"
                      : "bg-muted-foreground/40",
                  )}
                />
                <span>
                  {activeFile?.lastModified
                    ? `Guardado ${activeFile.lastModified}`
                    : "Guardado"}
                </span>
              </>
            )}
          </button>
          <div className="w-px h-2.5 bg-border" />
          <span>
            {wordCount}{" "}{wordCount === 1 ? "palabra" : "palabras"}
          </span>
          <div className="w-px h-2.5 bg-border" />
          <span>{readingMinutes} min de lectura</span>
        </div>

        <div className="flex items-center gap-2">
          <span>Panel {pane === "left" ? "Izquierdo" : "Derecho"}</span>
        </div>
      </footer>
    </div>
  );
}

// Welcome/Fallback view for a pane
export function WelcomeView({ pane }: { pane: "left" | "right" }) {
  const { files, openFile, createFile, setPaneViewType } = useWorkspace();
  const recentFiles = files.filter((f) => f.type === "file").slice(0, 5);

  const handleCreateQuick = () => {
    const newId = createFile("Nota sin título");
    openFile(newId, pane);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 bg-background/50 border-r border-border/10 text-center select-none h-full overflow-y-auto custom-scrollbar">
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[350px] h-[350px] bg-primary opacity-[0.03] blur-[80px] rounded-full pointer-events-none" />

      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-6">
        <FileText className="w-6 h-6" strokeWidth={1.5} />
      </div>

      <h3 className="text-lg font-semibold text-foreground mb-2 text-balance">
        Comienza a documentar
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed mb-7 text-pretty">
        Crea una nota nueva, abre un documento reciente o explora el grafo de
        conexiones. Usa{" "}
        <kbd className="px-1.5 py-0.5 rounded bg-secondary/80 border border-border/50 text-[10px] font-mono">
          ⌘K
        </kbd>{" "}
        para buscar en cualquier momento.
      </p>

      <div className="flex flex-col gap-2 w-full max-w-[260px]">
        <button
          onClick={handleCreateQuick}
          className="flex items-center justify-center gap-2 h-10 px-4 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 active:scale-[0.98] transition-all shadow-sm shadow-primary/15"
        >
          <Plus className="w-4 h-4" />
          Crear nota nueva
        </button>

        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={() => {
              const event = new KeyboardEvent("keydown", { key: "k", metaKey: true });
              document.dispatchEvent(event);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 h-9 px-3 bg-background border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-all"
          >
            <Search className="w-3.5 h-3.5" />
            Buscar
          </button>
          <button
            onClick={() => setPaneViewType("graph", pane)}
            className="flex-1 flex items-center justify-center gap-1.5 h-9 px-3 bg-background border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-all"
          >
            <Network className="w-3.5 h-3.5" />
            Ver grafo
          </button>
        </div>
      </div>

      {recentFiles.length > 0 && (
        <div className="mt-10 w-full max-w-[300px]">
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2.5 flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Documentos recientes
          </h4>
          <div className="space-y-1">
            {recentFiles.map((f) => (
              <div
                key={f.id}
                onClick={() => openFile(f.id, pane)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-secondary/10 hover:bg-secondary/50 text-xs font-medium text-foreground/80 hover:text-foreground cursor-pointer transition-all border border-border/10 hover:border-border/30"
              >
                <FileText className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />
                <span className="truncate flex-1 text-left">{f.name.replace(".md", "")}</span>
                {f.lastModified && (
                  <span className="text-[10px] text-muted-foreground/50 shrink-0 tabular-nums">
                    {f.lastModified}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-border/30 w-full max-w-[300px]">
        <p className="text-[10px] text-muted-foreground/60 leading-relaxed text-pretty">
          Usa{" "}
          <code className="px-1 py-0.5 rounded bg-secondary/60 border border-border/30 font-mono text-[9px]">
            [[Título]]
          </code>{" "}
          dentro de una nota para crear vínculos bidireccionales con otros documentos.
        </p>
      </div>
    </div>
  );
}
